import CheckCircle from '@/assets/icons/CheckCircle.svg';
import { MoreLikeThisButton } from '@/components/MoreLikeThisButton';
import { PromoBadge } from '@/components/PromoBadge';
import { useAddToBasket } from '@/hooks/useAddToBasket';
import type { Product } from '@/types';
import { buildCloudflareImageUrl } from '@/utils/buildCloudflareImageUrl';
import { getQueryId, getAuthUserToken } from '@/utils/searchAnalytics';
import { ProductTileCta } from './ProductTileCta';
import * as Styled from './ProductTile.styled';

const parsePrice = (value: string) =>
  parseFloat(value.replace(/[^0-9.]/g, '')) || 0;

interface ProductTileProps {
  hideCta?: boolean;
  hidePromotion?: boolean;
  index?: number;
  listName?: string;
  onAddToBasket?: () => void;
  onSelect?: () => void;
  product: Product;
  productNameElement?: 'div' | 'h3';
  showCta?: boolean;
  showMoreLikeThis?: boolean;
}

export const ProductTile = ({
  hideCta = false,
  hidePromotion = false,
  index,
  listName,
  onAddToBasket,
  onSelect,
  product,
  productNameElement,
  showMoreLikeThis = false,
}: ProductTileProps) => {
  const { addToBasket, isPending } = useAddToBasket(product, listName);

  const handleSelect = () => {
    onSelect?.();

    // Algolia insight: "Product Tile Clicked" — only when index is provided
    // (excludes modals/OtherFormats). Mirrors _AlgoliaInsightEvents.cshtml productClickGtmEventFromReact
    if (index !== undefined && product.sku) {
      const queryId = getQueryId() ?? '';
      const position = index + 1; // 1-based
      const eventName = queryId.length > 0
        ? 'Product Tile with Search Clicked'
        : 'Product Tile Clicked';

      window.dataLayer?.push({
        event: eventName,
        authUserToken: getAuthUserToken(),
        queryId,
        objectIds: [product.sku],
        positions: [position],
      });
    }
  };

  const handleAddToBasket = () => {
    if (!isPending) {
      const payload = {
        code: product.sku,
        quantity: 1,
      };

      onAddToBasket?.();
      addToBasket(payload);
    }
  };

  const isBook = product.productType === 'BookProduct';

  const imageSrc = buildCloudflareImageUrl(
    product.image?.thumbnailSrc ?? product.image?.mainSrc ?? '',
    { width: 172 },
  ) || undefined;

  if (!product.productUrl) {
    return null;
  }

  return (
    <Styled.Tile>
      <Styled.ImageContainer $isBook={isBook}>
        <Styled.ImageLink href={product.productUrl} title={product.name ?? ''} handler={handleSelect}>
          <Styled.ProductImage
            $isBook={isBook}
            alt={(product.image?.altText || product.name) ?? ''}
            width={172}
            height={216}
            loading="lazy"
            src={imageSrc}
          />
        </Styled.ImageLink>
        {product.promoBadge?.src ? (
          <Styled.BadgeWrapper>
            <PromoBadge
              altText={product.promoBadge.altText}
              src={product.promoBadge.src}
            />
          </Styled.BadgeWrapper>
        ) : null}
      </Styled.ImageContainer>

      <Styled.ContentWrap>
        <Styled.MetaLink href={product.productUrl} title={product.name ?? ''} handler={handleSelect}>
          <Styled.Title as={productNameElement}>{product.name}</Styled.Title>
          {/* Always render so the author row reserves a consistent height
              (Styled.Author min-height) even when a product has no author,
              keeping price/CTA aligned across tiles. */}
          <Styled.Author>{product.author}</Styled.Author>
          <Styled.Price>
            {product.price ? (
              <>
                {product.price.current}
                {product.price.former && parsePrice(product.price.former) > parsePrice(product.price.current) ? (
                  <Styled.OldPrice>{product.price.former}</Styled.OldPrice>
                ) : null}
              </>
            ) : null}
          </Styled.Price>
        </Styled.MetaLink>

        {hideCta ? null : (
          <ProductTileCta
            handler={handleAddToBasket}
            isPending={isPending}
            product={product}
          />
        )}

        {showMoreLikeThis ? (
          <MoreLikeThisButton
            productName={product.name}
            productUrl={product.productUrl}
          />
        ) : null}

        {!hidePromotion ? (
          <Styled.PromoChip $purchaseAction={product.purchaseAction} $isEmpty={!product.promotion}>
            {product.promotion ? (
              <>
                <CheckCircle />
                <span>{product.promotion}</span>
              </>
            ) : null}
          </Styled.PromoChip>
        ) : null}
      </Styled.ContentWrap>
    </Styled.Tile>
  );
};
